import { isAxiosError } from 'axios';
import axiosInstance from '../config/axios.config';
import { API_ENDPOINTS } from '../config/api.config';
import { storage } from '../utils/storage';

import type { PermissionType } from '../sections/permission/enum/PermissionType';
import type { PermissionStatus } from '../sections/permission/enum/PermissionStatus';
import type { EmployeeDto } from './api_data';

// Backend'deki LeaveRequest modeline karşılık gelen interface
export interface Permission {
  id: string;                  // UUID
  employee: EmployeeDto;       // Employee object
  startDate: string;          // LocalDate
  endDate: string;           // LocalDate
  leaveType: PermissionType;  // LeaveType enum
  status: PermissionStatus;   // LeaveStatus enum
  reason: string;
  createdAt: string;         // LocalDateTime
  approvedAt?: string;       // LocalDateTime
  approvedBy?: string;       // UUID
}

// Backend'deki CreateLeaveRequestDto'ya karşılık gelen interface
export interface CreateLeaveRequestDto {
  employeeId: string;        // UUID
  startDate: string;        // LocalDate (YYYY-MM-DD)
  endDate: string;         // LocalDate (YYYY-MM-DD)
  leaveType: PermissionType;
  reason: string;
}

// Backend'deki LeaveApprovalDto'ya karşılık gelen interface
export interface LeaveApprovalDto {
  status: PermissionStatus;
  approvedBy?: string;
}

// İzin güncelleme için DTO
export interface UpdateLeaveRequestDto {
  id: string;             // UUID
  startDate: string;      // LocalDate (YYYY-MM-DD)
  endDate: string;        // LocalDate (YYYY-MM-DD)
  leaveType: PermissionType;
  reason: string;
  status: PermissionStatus;
}

// Bekleyen izin taleplerini getir
export const getPendingPermissions = async (userId: string): Promise<Permission[]> => {
  const response = await axiosInstance.get<Permission[]>(
    `${API_ENDPOINTS.LEAVE}/pending/${userId}`
  );

  return response.data;
};

// Yeni izin talebi oluştur
export const createPermission = async (data: CreateLeaveRequestDto): Promise<Permission> => {
  try {
    const response = await axiosInstance.post<Permission>(
      `${API_ENDPOINTS.LEAVE}/request`,
      data,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${storage.get('token')}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      if (error.response?.status === 401) {
        storage.remove('token');
        window.location.href = '/api/auth/login';
      }
    }
    throw error;
  }
};

// İzin talebini onayla veya reddet
export const updatePermissionStatus = async (id: string, data: LeaveApprovalDto): Promise<Permission> => {
  try {
    const response = await axiosInstance.put<Permission>(
      `${API_ENDPOINTS.LEAVE}/request/${id}/approve`,
      data,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${storage.get('token')}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      if (error.response?.status === 401) {
        storage.remove('token');
        window.location.href = '/api/auth/login';
      }
    }
    throw error;
  }
};

// Çalışanın izin taleplerini getir
export const getEmployeePermissions = async (employeeId: string): Promise<Permission[]> => {
  try {
    const response = await axiosInstance.get<Permission[]>(
      `${API_ENDPOINTS.LEAVE}/employee/${employeeId}`,
      {
        headers: {
          Authorization: `Bearer ${storage.get('token')}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      if (error.response?.status === 401) {
        storage.remove('token');
        window.location.href = '/api/auth/login';
      }
    }
    throw error;
  }
};

// Kullanıcının sorumlu olduğu çalışanların izin taleplerini getir
export const getUserPermissions = async (userId: string): Promise<Permission[]> => {
  try {
    const token = storage.get('token');
    if (!token) {
      throw new Error('Token bulunamadı');
    }

    const response = await axiosInstance.get<Permission[]>(
      `${API_ENDPOINTS.LEAVE}/user/${userId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Accept': '*/*'
        },
      }
    );

    if (response.status === 204) {
      return [];
    }

    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      if (error.response?.status === 401) {
        storage.remove('token');
        window.location.href = '/api/auth/login';
      }
    }
    throw error;
  }
};

export const getUserPermissionsAll = async (): Promise<{ content: Permission[] }> => {
  try {
    const token = storage.get('token');
    if (!token) {
      throw new Error('Token bulunamadı');
    }

    const response = await axiosInstance.get<Permission[]>(
      `${API_ENDPOINTS.LEAVE}/my-leaves`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Accept': '*/*'
        },
      }
    );

    if (response.status === 204) {
      return { content: [] };
    }

    return response.data as any;
  } catch (error) {
    if (isAxiosError(error)) {
      if (error.response?.status === 401) {
        storage.remove('token');
        window.location.href = '/auth/login';
      }
    }
    throw error;
  }
};
// İzin detaylarını getir
export const getPermissionById = async (id: string): Promise<Permission> => {
  try {
    const token = storage.get('token');
    if (!token) {
      throw new Error('Token bulunamadı');
    }

    const response = await axiosInstance.get<Permission>(
      `${API_ENDPOINTS.LEAVE}/getLeave/${id}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Accept': '*/*'
        },
      }
    );

    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      if (error.response?.status === 401) {
        storage.remove('token');
        window.location.href = '/api/auth/login';
      }
      if (error.response?.status === 404) {
        throw new Error('İzin talebi bulunamadı');
      }
    }
    throw error;
  }
};

// İzin talebini güncelle
export const updatePermission = async (data: UpdateLeaveRequestDto): Promise<Permission> => {
  try {
    const token = storage.get('token');
    if (!token) {
      throw new Error('Token bulunamadı');
    }

    const response = await axiosInstance.put<Permission>(
      `${API_ENDPOINTS.LEAVE}/update`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      if (error.response?.status === 401) {
        storage.remove('token');
        window.location.href = '/api/auth/login';
      }
      if (error.response?.status === 404) {
        throw new Error('İzin talebi bulunamadı');
      }
    }
    throw error;
  }
};

// İzin talebini sil
export const deletePermission = async (id: string): Promise<boolean> => {
  try {
    const token = storage.get('token');
    if (!token) {
      throw new Error('Token bulunamadı');
    }

    const response = await axiosInstance.delete(
      `${API_ENDPOINTS.LEAVE}/${id}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return response.status === 204;
  } catch (error) {
    if (isAxiosError(error)) {
      if (error.response?.status === 401) {
        storage.remove('token');
        window.location.href = '/api/auth/login';
      }
    }
    throw error;
  }
};

// Export interfaces ve fonksiyonları
export interface ExportLeaveParams {
  format: 'PDF' | 'EXCEL';
  startDate?: string;
  endDate?: string;
  branchId?: string;
  fileName?: string;
  exportType?: 'current' | 'custom' | 'branch' | 'all';
}

// Manager/Admin için izin export - BACKEND API İLE ENTEGRE
export const exportLeaveRequests = async (params: ExportLeaveParams): Promise<Blob> => {
  try {
    const token = storage.get('token');
    if (!token) {
      throw new Error('Token bulunamadı');
    }

    // Query parametrelerini oluştur
    const queryParams = new URLSearchParams();
    
    // Backend enum değerleri: PDF, EXCEL
    queryParams.append('format', params.format);
    
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.branchId) queryParams.append('branchId', params.branchId);
    if (params.fileName) queryParams.append('fileName', params.fileName);

    const response = await axiosInstance.get(
      `/api/export/leaves?${queryParams.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Accept': '*/*'
        },
        responseType: 'blob', // Dosya indirme için blob response
      }
    );

    return response.data;
    
  } catch (error) {
    if (isAxiosError(error)) {
      if (error.response?.status === 401) {
        storage.remove('token');
        window.location.href = '/api/auth/login';
        throw new Error('Oturum süresi dolmuş. Lütfen tekrar giriş yapın.');
      }
      if (error.response?.status === 403) {
        throw new Error('Bu işlem için yetkiniz bulunmuyor.');
      }
      if (error.response?.status === 400) {
        throw new Error('Geçersiz parametreler. Lütfen kontrol edin.');
      }
    }
    throw new Error('Export işlemi sırasında bir hata oluştu');
  }
};

// Super Admin için tüm izinleri export - BACKEND API İLE ENTEGRE
export const exportAllLeaveRequests = async (params: ExportLeaveParams): Promise<Blob> => {
  try {
    const token = storage.get('token');
    if (!token) {
      throw new Error('Token bulunamadı');
    }

    // Query parametrelerini oluştur
    const queryParams = new URLSearchParams();
    
    // Backend enum değerleri: PDF, EXCEL
    queryParams.append('format', params.format);
    
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.fileName) queryParams.append('fileName', params.fileName);

    const response = await axiosInstance.get(
      `/api/export/leaves/all?${queryParams.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Accept': '*/*'
        },
        responseType: 'blob', // Dosya indirme için blob response
      }
    );

    return response.data;
    
  } catch (error) {
    if (isAxiosError(error)) {
      if (error.response?.status === 401) {
        storage.remove('token');
        window.location.href = '/api/auth/login';
        throw new Error('Oturum süresi dolmuş. Lütfen tekrar giriş yapın.');
      }
      if (error.response?.status === 403) {
        throw new Error('Bu işlem için Super Admin yetkisi gerekiyor.');
      }
      if (error.response?.status === 400) {
        throw new Error('Geçersiz parametreler. Lütfen kontrol edin.');
      }
    }
    throw new Error('Super Admin export işlemi sırasında bir hata oluştu');
  }
};

// Mock fonksiyonlar kaldırıldı - Artık backend API'leri kullanıyoruz
// Backend'den gelen PDF ve Excel dosyaları otomatik olarak doğru formatta gelecek

// File download helper
export const downloadFile = (blob: Blob, fileName: string, format: 'PDF' | 'EXCEL') => {
  try {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // Backend'den gelen dosya adını kullan, yoksa default oluştur
    let finalFileName = fileName;
    if (!fileName || !fileName.includes('.')) {
      const extension = format === 'PDF' ? '.pdf' : '.xlsx';
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '');
      finalFileName = `izin-talepleri-${timestamp}${extension}`;
    }
    
    link.download = finalFileName;
    
    // Dosyayı indir
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Memory cleanup
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
    }, 100);
    
  } catch (error) {
    throw new Error('Dosya indirilemedi');
  }
};

// Backend'den yeni endpoint: Kullanıcının yönettiği çalışanların izin taleplerini getir (pagination ile)
export const getLeaveRequestsForUser = async (userId: string, page: number = 0, size: number = 10, sortBy: string = 'createdAt', sortDirection: string = 'desc'): Promise<{content: Permission[], totalElements: number, totalPages: number, size: number, number: number}> => {
  try {
    const token = storage.get('token');
    if (!token) {
      throw new Error('Token bulunamadı');
    }

    const response = await axiosInstance.get(`${API_ENDPOINTS.LEAVE}/for-user/${userId}`, {
      params: {
        page,
        size,
        sortBy,
        sortDirection
      },
      headers: {
        Authorization: `Bearer ${token}`,
        'Accept': '*/*'
      }
    });

    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      if (error.response?.status === 401) {
        storage.remove('token');
        window.location.href = '/sign-in';
        throw new Error('Oturum süresi dolmuş. Lütfen tekrar giriş yapın.');
      }
      if (error.response?.status === 403) {
        throw new Error('Bu işlem için yetkiniz bulunmuyor.');
      }
      throw new Error(error.response?.data?.message || 'İzin talepleri alınırken hata oluştu');
    }
    throw new Error('İzin talepleri alınırken hata oluştu');
  }
}; // Backend'den yeni endpoint: Kullanıcının yönettiği çalışanların izin taleplerini getir (pagination ile)
export const getLeaveRequestsByBranch = async (branchId: string, page: number = 0, size: number = 10, sortBy: string = 'createdAt', sortDirection: string = 'desc'): Promise<{content: Permission[], totalElements: number, totalPages: number, size: number, number: number}> => {
  try {
    const token = storage.get('token');
    if (!token) {
      throw new Error('Token bulunamadı');
    }

    const response = await axiosInstance.get(`${API_ENDPOINTS.LEAVE}/by-branch/${branchId}`, {
      params: {
        page,
        size,
        sortBy,
        sortDirection
      },
      headers: {
        Authorization: `Bearer ${token}`,
        'Accept': '*/*'
      }
    });

    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      if (error.response?.status === 401) {
        storage.remove('token');
        window.location.href = '/sign-in';
        throw new Error('Oturum süresi dolmuş. Lütfen tekrar giriş yapın.');
      }
      if (error.response?.status === 403) {
        throw new Error('Bu işlem için yetkiniz bulunmuyor.');
      }
      throw new Error(error.response?.data?.message || 'İzin talepleri alınırken hata oluştu');
    }
    throw new Error('İzin talepleri alınırken hata oluştu');
  }
}; 